<template>
  <div class="ma-4 px-4">
    <v-row>
      <v-col cols="12" class="d-flex align-center">
        <div class="text-caption grey--text">
          Controls the sidebar's structure — grouping, icons, order and which
          page each item links to. Access (who can see an item) is still
          governed by Roles; this only controls layout.
        </div>
        <v-spacer></v-spacer>
        <v-btn small outlined class="mr-2" @click="openModuleDialog">
          <v-icon left small>mdi-file-plus-outline</v-icon>
          Add Module
        </v-btn>
        <v-btn small class="primary" @click="openCreate(null)">
          <v-icon left small>mdi-plus</v-icon>
          Add Top-Level Item
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-sheet outlined class="pa-2">
          <v-treeview
            :items="treeItems"
            item-key="id"
            item-text="title"
            item-children="children"
            open-on-click
            dense
          >
            <template #prepend="{ item }">
              <v-icon color="primary">{{ item.icon || "mdi-menu" }}</v-icon>
            </template>
            <template #label="{ item }">
              <div class="d-flex align-center py-1">
                <span class="mr-2">{{ item.title }}</span>
                <v-chip x-small v-if="item.module_name" color="accent" text-color="white">
                  {{ item.module_name }}
                </v-chip>
                <v-chip x-small v-else outlined>Group</v-chip>
                <v-chip x-small v-if="!item.visible" class="ml-1" color="grey" text-color="white">
                  Hidden
                </v-chip>
                <v-spacer></v-spacer>
                <v-btn icon x-small :disabled="!canMoveUp(item)" @click.stop="move(item, -1)">
                  <v-icon small>mdi-arrow-up</v-icon>
                </v-btn>
                <v-btn icon x-small :disabled="!canMoveDown(item)" @click.stop="move(item, 1)">
                  <v-icon small>mdi-arrow-down</v-icon>
                </v-btn>
                <v-btn
                  icon
                  x-small
                  v-if="!item.parent_id"
                  @click.stop="openCreate(item.id)"
                >
                  <v-icon small>mdi-plus</v-icon>
                </v-btn>
                <v-btn icon x-small @click.stop="openEdit(item)">
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
                <v-btn icon x-small @click.stop="confirmDelete(item)">
                  <v-icon small color="error">mdi-delete</v-icon>
                </v-btn>
              </div>
            </template>
          </v-treeview>
        </v-sheet>
      </v-col>
    </v-row>

    <v-dialog v-model="dialog" width="500" persistent>
      <v-card class="secondary" v-if="dialog">
        <v-form ref="form">
          <v-card-title class="font-weight-medium secondary--text primary">
            {{ editMode ? "Edit Menu Item" : "Add Menu Item" }}
            <v-spacer></v-spacer>
            <v-btn small icon @click="dialog = false">
              <v-icon small color="secondary">mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="pt-5">
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model.trim="form.title"
                  label="Title*"
                  outlined
                  dense
                  hide-details="auto"
                  :rules="[rules.required]"
                />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model.trim="form.icon"
                  label="Icon"
                  hint="Material Design Icon name, e.g. mdi-cash"
                  persistent-hint
                  outlined
                  dense
                  hide-details="auto"
                  :prepend-icon="form.icon || 'mdi-help-circle-outline'"
                />
              </v-col>
              <v-col cols="12">
                <v-autocomplete
                  v-model="form.module_id"
                  :items="modules"
                  item-text="name"
                  item-value="id"
                  label="Links to page"
                  hint="Leave blank for a group header with no page of its own"
                  persistent-hint
                  outlined
                  dense
                  clearable
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12">
                <v-switch
                  v-model="form.visible"
                  label="Show in sidebar"
                  hint="Turn off to keep this page reachable and grantable via Roles without showing it as a nav link"
                  persistent-hint
                  hide-details="auto"
                  class="mt-0"
                />
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions class="pt-0 mb-3 mx-2">
            <v-spacer></v-spacer>
            <v-btn class="primary" @click="save">{{
              editMode ? "Update" : "Save"
            }}</v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <v-dialog v-model="moduleDialog" width="400" persistent>
      <v-card class="secondary" v-if="moduleDialog">
        <v-form ref="moduleForm">
          <v-card-title class="font-weight-medium secondary--text primary">
            Add Module
            <v-spacer></v-spacer>
            <v-btn small icon @click="moduleDialog = false">
              <v-icon small color="secondary">mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="pt-5">
            <div class="text-caption grey--text mb-3">
              Registers a new page so it can be linked from a menu item below.
              This alone adds nothing to the sidebar — use "Add Top-Level
              Item" (or "+" under a group) afterwards to place it.
            </div>
            <v-text-field
              v-model.trim="newModuleName"
              label="Page name*"
              hint="Must match the route name in router/index.js"
              persistent-hint
              outlined
              dense
              hide-details="auto"
              :rules="[rules.required]"
              @keyup.enter="createModule"
            />
          </v-card-text>
          <v-card-actions class="pt-0 mb-3 mx-2">
            <v-spacer></v-spacer>
            <v-btn class="primary" @click="createModule">Save</v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" width="400" persistent>
      <v-card class="secondary">
        <v-card-title class="primary white--text">Confirmation</v-card-title>
        <v-card-text class="mt-4">
          <div class="subtitle-2">
            Delete "{{ deleteTarget && deleteTarget.title }}"?
            <span v-if="deleteTarget && deleteTarget.children && deleteTarget.children.length">
              This will also delete its {{ deleteTarget.children.length }} sub-item(s).
            </span>
          </div>
        </v-card-text>
        <v-card-actions class="mt-n3 d-flex justify-end">
          <v-btn text color="green" @click="deleteConfirmed">Yes</v-btn>
          <v-btn text color="red" @click="deleteDialog = false">No</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { rules } from "@/helpers/rules";

export default {
  data() {
    return {
      rules,
      menuItems: [],
      modules: [],
      dialog: false,
      editMode: false,
      form: { title: "", icon: "", module_id: null, visible: true },
      createParentId: null,
      editingId: null,
      deleteDialog: false,
      deleteTarget: null,
      moduleDialog: false,
      newModuleName: "",
    };
  },
  computed: {
    treeItems() {
      const byId = {};
      this.menuItems.forEach((item) => {
        byId[item.id] = { ...item, children: [] };
      });
      const roots = [];
      this.menuItems.forEach((item) => {
        const node = byId[item.id];
        if (item.parent_id && byId[item.parent_id]) {
          byId[item.parent_id].children.push(node);
        } else if (!item.parent_id) {
          roots.push(node);
        }
      });
      const bySortOrder = (a, b) => a.sort_order - b.sort_order;
      roots.sort(bySortOrder);
      roots.forEach((root) => root.children.sort(bySortOrder));
      return roots;
    },
  },
  created() {
    this.$store.commit("setBreadcrumbs", [{ text: "Menu Builder" }]);
    this.initialise();
  },
  methods: {
    initialise() {
      this.getMenuItems();
      this.getModules();
    },
    async getMenuItems() {
      await this.$axios.get("menu_items").then((res) => {
        this.menuItems = res.data.data;
      });
    },
    async getModules() {
      await this.$axios.get("modules").then((res) => {
        this.modules = res.data.data;
      });
    },
    siblingsOf(item) {
      return this.menuItems
        .filter((m) => m.parent_id === item.parent_id)
        .sort((a, b) => a.sort_order - b.sort_order);
    },
    canMoveUp(item) {
      const siblings = this.siblingsOf(item);
      return siblings.findIndex((s) => s.id === item.id) > 0;
    },
    canMoveDown(item) {
      const siblings = this.siblingsOf(item);
      const index = siblings.findIndex((s) => s.id === item.id);
      return index >= 0 && index < siblings.length - 1;
    },
    async move(item, direction) {
      const siblings = this.siblingsOf(item);
      const index = siblings.findIndex((s) => s.id === item.id);
      const swapWith = siblings[index + direction];
      if (!swapWith) return;

      await this.$axios
        .put("menu_items/reorder", {
          items: [
            { id: item.id, parent_id: item.parent_id, sort_order: swapWith.sort_order },
            { id: swapWith.id, parent_id: swapWith.parent_id, sort_order: item.sort_order },
          ],
        })
        .then(() => this.getMenuItems())
        .catch(async (error) => {
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response
          );
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        });
    },
    openModuleDialog() {
      this.newModuleName = "";
      this.moduleDialog = true;
    },
    async createModule() {
      if (!this.newModuleName) return;

      await this.$axios
        .post("modules", { module: { name: this.newModuleName } })
        .then(() => {
          this.$store.commit("showSnackbar", {
            message: "Module created successfully",
            color: "success",
          });
          this.moduleDialog = false;
          this.getModules();
        })
        .catch(async (error) => {
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response
          );
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        });
    },
    openCreate(parentId) {
      this.editMode = false;
      this.createParentId = parentId;
      this.editingId = null;
      this.form = { title: "", icon: "", module_id: null, visible: true };
      this.dialog = true;
    },
    openEdit(item) {
      this.editMode = true;
      this.editingId = item.id;
      this.form = {
        title: item.title,
        icon: item.icon,
        module_id: item.module_id,
        visible: item.visible,
      };
      this.dialog = true;
    },
    save() {
      if (!this.$refs.form.validate()) return;

      if (this.editMode) {
        this.updateMenuItem();
      } else {
        this.createMenuItem();
      }
    },
    async createMenuItem() {
      const siblings = this.menuItems.filter(
        (m) => m.parent_id === this.createParentId
      );
      const nextSortOrder =
        siblings.reduce((max, s) => Math.max(max, s.sort_order), 0) + 10;

      await this.$axios
        .post("menu_items", {
          menu_item: { ...this.form, parent_id: this.createParentId, sort_order: nextSortOrder },
        })
        .then(() => {
          this.$store.commit("showSnackbar", {
            message: "Menu item created successfully",
            color: "success",
          });
          this.dialog = false;
          this.getMenuItems();
        })
        .catch(async (error) => {
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response
          );
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        });
    },
    async updateMenuItem() {
      await this.$axios
        .put("menu_items/" + this.editingId, { menu_item: this.form })
        .then(() => {
          this.$store.commit("showSnackbar", {
            message: "Menu item updated successfully",
            color: "success",
          });
          this.dialog = false;
          this.getMenuItems();
        })
        .catch(async (error) => {
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response
          );
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        });
    },
    confirmDelete(item) {
      this.deleteTarget = item;
      this.deleteDialog = true;
    },
    async deleteConfirmed() {
      await this.$axios
        .delete("menu_items/" + this.deleteTarget.id)
        .then(() => {
          this.$store.commit("showSnackbar", {
            message: "Menu item deleted successfully",
            color: "success",
          });
          this.deleteDialog = false;
          this.getMenuItems();
        })
        .catch(async (error) => {
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response
          );
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        });
    },
  },
};
</script>

<style></style>
